import { Link } from "react-router-dom";
import { useSEO } from "@/hooks/useSEO";
import { profileData } from "@/mocks/profile";
import Breadcrumb from "@/components/feature/Breadcrumb";

export default function ProfilePage() {
  useSEO({
    title: "小泽玛利亚个人资料 - 明星资料档案与成长历程",
    description:
      "小泽玛利亚完整个人资料：出生日期、职业、代表作品、粉丝规模、成长里程碑与官方社交账号，粉丝了解偶像的权威入口。",
    keywords: "小泽玛利亚,个人资料,明星档案,成长历程,代表作品,个人简介",
  });

  return (
    <div className="animate-fade-up">
      <div className="relative w-full h-[220px] md:h-[320px] overflow-hidden bg-background-100">
        <img
          src={profileData.cover}
          alt="小泽玛利亚资料页banner"
          title="小泽玛利亚个人资料"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
      </div>

      <div className="mx-auto max-w-content px-4 md:px-6">
        <div className="-mt-16 md:-mt-20 relative">
          <div className="flex flex-col sm:flex-row sm:items-end gap-5">
            <div className="w-32 h-32 md:w-40 md:h-40 rounded-lg overflow-hidden border-4 border-background-50 bg-background-100 shrink-0">
              <img
                src={profileData.avatar}
                alt={profileData.name}
                title="小泽玛利亚个人头像"
                className="w-full h-full object-top object-cover"
              />
            </div>
            <div className="pb-2">
              <span className="text-xs font-medium tracking-[0.2em] text-primary-600 uppercase">
                Profile
              </span>
              <h1 className="font-heading text-2xl md:text-4xl font-semibold text-foreground-950">
                {profileData.name}
              </h1>
              <p className="text-sm text-foreground-500 mt-1">{profileData.englishName}</p>
              <p className="text-sm text-primary-700 mt-2">{profileData.tagline}</p>
            </div>
          </div>
        </div>

        <div className="mt-8 md:mt-10">
          <Breadcrumb items={[{ label: "个人资料" }]} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-10 mt-6">
          <div className="lg:col-span-2 flex flex-col gap-10">
            <section>
              <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mb-4 flex items-center gap-2">
                <i className="ri-file-user-line text-primary-600"></i>
                个人简介
              </h2>
              <div className="flex flex-col gap-4">
                {profileData.intro.map((p, idx) => (
                  <p key={idx} className="text-sm md:text-base text-foreground-700 leading-loose">
                    {p}
                  </p>
                ))}
              </div>
            </section>

            <section>
              <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950 mb-5 flex items-center gap-2">
                <i className="ri-route-line text-accent-600"></i>
                成长里程碑
              </h2>
              <div className="relative pl-6 border-l-2 border-primary-100">
                {profileData.milestones.map((m, idx) => (
                  <div key={m.year} className={`relative ${idx === profileData.milestones.length - 1 ? "" : "pb-7"}`}>
                    <span className="absolute -left-[31px] top-1 w-4 h-4 rounded-full bg-accent-500 border-4 border-background-50"></span>
                    <div className="flex items-center gap-3 mb-1 flex-wrap">
                      <span className="font-heading text-lg font-semibold text-primary-700">{m.year}</span>
                      <span className="text-sm font-medium text-foreground-950">{m.title}</span>
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed">{m.description}</p>
                  </div>
                ))}
              </div>
            </section>
          </div>

          <div className="lg:col-span-1 flex flex-col gap-6">
            <section className="bg-background-50 border border-background-200 rounded-lg p-5">
              <h2 className="font-heading text-lg font-semibold text-foreground-950 mb-4 flex items-center gap-2">
                <i className="ri-id-card-line text-primary-600"></i>
                基本资料
              </h2>
              <dl className="flex flex-col">
                {profileData.basicInfo.map((row, idx) => (
                  <div
                    key={row.label}
                    className={`flex items-start justify-between gap-4 py-2.5 ${
                      idx === 0 ? "" : "border-t border-background-100"
                    }`}
                  >
                    <dt className="text-sm text-foreground-500 shrink-0">{row.label}</dt>
                    <dd className="text-sm text-foreground-900 text-right">{row.value}</dd>
                  </div>
                ))}
              </dl>
            </section>

            <section className="bg-primary-50 border border-primary-100 rounded-lg p-5">
              <h2 className="font-heading text-lg font-semibold text-foreground-950 mb-4 flex items-center gap-2">
                <i className="ri-link-m text-primary-600"></i>
                相关链接
              </h2>
              <div className="flex flex-col gap-2.5">
                {profileData.socialLinks.map((link) => (
                  <a
                    key={link.label}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 bg-background-50 border border-background-200 rounded-md px-3.5 py-2.5 hover:border-primary-300 transition-colors cursor-pointer"
                  >
                    <span className="w-9 h-9 rounded-md bg-primary-100 text-primary-700 flex items-center justify-center shrink-0">
                      <i className={link.icon}></i>
                    </span>
                    <span className="min-w-0">
                      <span className="block text-sm text-foreground-900">{link.label}</span>
                      <span className="block text-xs text-foreground-500 truncate">{link.handle}</span>
                    </span>
                  </a>
                ))}
              </div>
            </section>

            <Link
              to="/works"
              className="inline-flex items-center justify-center gap-1.5 px-5 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-film-line"></i>
              浏览影视作品
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}